// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@mcmc/MobiusOverrides/LazyImage";
import { RobotoCondensed } from "@mcmc/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showCategoryImage = "showCategoryImage",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
    sectionsWrapper?: InjectableCss;
    leftSectionWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    bgCategoryWrapper?: InjectableCss;
    categoryImage?: LazyImageProps;
    categoryName?: TypographyPresentationProps;
    categoryDescription?: TypographyPresentationProps;
}

export const bannerStyles: CategoryDetailBannerStyles = {
    container: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    sectionsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex: 1;
            margin: -15px;
        `,
    },
    leftSectionWrapper: {
        css: css`
            flex: 1 1 445px;
            padding: 15px;
        `,
    },
    rightSectionWrapper: {
        css: css`
            flex: 1 1 350px;
            padding: 15px;
            font-size: 16px;
            line-height: 24px;
            letter-spacing: 0.19px;
            html,
            body,
            p {
                font-size: inherit;
                line-height: inherit;
                letter-spacing: inherit;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    bgCategoryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 35px 33px;
            flex: 0 0 100%;
            position: relative;
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center center;
            align-items: stretch;
            min-height: 245px;
            height: 100%;
        `,
    },
    categoryImage: {
        css: css`
            position: absolute;
            top: 0;
            width: 0;
            height: 0;
            pointer-events: none;
            visibility: hidden;
        `,
    },
    categoryName: {
        variant: "h2",
        ...RobotoCondensed,
        size: "42px",
        lineHeight: "42px",
        weight: "bold",
        color: "text.inverse",
        css: css`
            margin-bottom: 10px;
        `,
    },
    categoryDescription: {
        color: "text.inverse",
        size: "16px",
        weight: 500,
        lineHeight: "22px",
    },
};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, fields }: Props) => {
    const [bgImgUrl, setBgImgUrl] = React.useState(null);

    if (!category) {
        return null;
    }

    const { htmlContent, largeImagePath, name, shortDescription } = category;
    const { showCategoryImage } = fields;
    const hasImage = showCategoryImage && largeImagePath;

    const bgCategoryWrapper = {
        css: css`
            ${styles.bgCategoryWrapper?.css}
            background-image: url(${bgImgUrl});
        `,
    };

    if (!(hasImage || htmlContent)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="categoryDetailBanner_htmlContent">
            <StyledWrapper {...styles.sectionsWrapper}>
                {hasImage && (
                    <StyledWrapper {...styles.leftSectionWrapper}>
                        <StyledWrapper {...bgCategoryWrapper}>
                            {name && <Typography {...styles.categoryName}>{name}</Typography>}
                            {shortDescription && (
                                <Typography {...styles.categoryDescription}>{shortDescription}</Typography>
                            )}
                            <LazyImage
                                {...styles.categoryImage}
                                src={largeImagePath}
                                onLoad={(newImagePath: any) => {
                                    setBgImgUrl(newImagePath);
                                }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {htmlContent && (
                    <StyledWrapper {...styles.rightSectionWrapper}>{parse(htmlContent, parserOptions)}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailBanner),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showCategoryImage,
                displayName: "Show Category Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
